import { useEffect } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { AuthLayout } from "../components/layout/AuthLayout.js";
import { useSetupStatus } from "../components/guards.js";
import { Button } from "../components/ui/Button.js";
import { Checkbox } from "@primereact/ui/checkbox";
import { Field } from "../components/ui/Field.js";
import { InputText as Input } from "@primereact/ui/inputtext";
import { isApiError } from "../lib/api.js";
import { useLogin, useMe } from "../lib/auth.js";
import { translateError } from "../lib/form.js";

const loginSchema = z.object({
  identifier: z.string().trim().min(1, "validation.required"),
  password: z.string().min(1, "validation.required"),
  rememberMe: z.boolean(),
});

type LoginForm = z.infer<typeof loginSchema>;

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setup = useSetupStatus();
  const me = useMe();
  const login = useLogin();

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { identifier: "", password: "", rememberMe: false },
  });

  const from = searchParams.get("from");
  const target =
    from && from.startsWith("/") && !from.startsWith("//") ? from : "/";

  useEffect(() => {
    if (setup.data && !setup.data.completed)
      navigate("/setup", { replace: true });
  }, [setup.data, navigate]);

  if (me.data) return <Navigate to={target} replace />;

  const onSubmit = handleSubmit(async (values) => {
    try {
      await login.mutateAsync(values);
      navigate(target, { replace: true });
    } catch (error) {
      const key =
        isApiError(error) && error.status === 401
          ? "errors.invalidCredentials"
          : isApiError(error) && error.status === 429
            ? "errors.tooManyAttempts"
            : "errors.generic";
      setError("root", { message: t(key) });
    }
  });

  return (
    <AuthLayout
      title={t("auth.login.title")}
      subtitle={t("auth.login.subtitle")}
    >
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        <Field
          label={t("auth.login.email")}
          htmlFor="identifier"
          error={translateError(errors.identifier?.message, t)}
          required
        >
          <Input
            id="identifier"
            autoComplete="username"
            className="w-full"
            invalid={!!errors.identifier}
            {...register("identifier")}
          />
        </Field>
        <Field
          label={t("auth.login.password")}
          htmlFor="password"
          error={translateError(errors.password?.message, t)}
          required
        >
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            className="w-full"
            invalid={!!errors.password}
            {...register("password")}
          />
        </Field>
        <div className="flex items-center justify-between gap-3">
          <label
            htmlFor="remember-me"
            className="flex items-center gap-2 text-sm text-app-text"
          >
            <Checkbox.Root
              inputId="remember-me"
              checked={watch("rememberMe")}
              onCheckedChange={(event: { checked: boolean }) =>
                setValue("rememberMe", event.checked, { shouldDirty: true })
              }
            >
              <Checkbox.Box>
                <Checkbox.Indicator match="checked">
                  <i className="pi pi-check" />
                </Checkbox.Indicator>
              </Checkbox.Box>
            </Checkbox.Root>
            <span>{t("auth.login.rememberMe")}</span>
          </label>
          <Link
            to="/forgot-password"
            className="text-sm font-medium text-brand hover:underline"
          >
            {t("auth.login.forgot")}
          </Link>
        </div>
        {errors.root ? (
          <p
            role="alert"
            className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger"
          >
            {errors.root.message}
          </p>
        ) : null}
        <Button
          type="submit"
          size="large"
          loading={isSubmitting || undefined}
          className="w-full"
        >
          {isSubmitting ? t("auth.login.submitting") : t("auth.login.submit")}
        </Button>
      </form>
    </AuthLayout>
  );
}
